import type { Box3, Scene } from "three";

/**
 * Fraction of the patch that is fully opaque before the rim fade begins. It
 * lives here rather than in `StudioFloor`, which imports it as the default for
 * its `solid` prop, because the radius policy below is what decides whether
 * the product lands inside that core.
 */
export const STUDIO_FLOOR_SOLID = 0.45;

/**
 * Smallest patch, in metres. Only a floor for a product small enough that a
 * patch scaled to it would be a coin under the case. Ordinary sizes are all
 * above it, so the floor tracks them proportionally rather than sitting on
 * this.
 */
export const STUDIO_FLOOR_MIN_RADIUS = 0.9;

/**
 * Patch for a scene whose product has not been measured yet, in metres: the
 * fixed radius the floor had before it tracked anything.
 *
 * Deliberately not {@link STUDIO_FLOOR_MIN_RADIUS}. "Nothing measured" and
 * "measured, and tiny" are different states that happen to arrive as the same
 * zero. The aligner does not publish a footprint until the product's box is
 * non-empty, and part geometry lands in a layout effect, so the first frame of
 * a canvas can draw the floor before anything has been measured. Falling back
 * to the minimum there would put a 1.8 m patch under a case that needs five,
 * which the interactive viewer would show as a pop and a preview or quote
 * image — rendered by the same stage — could capture outright.
 */
export const STUDIO_FLOOR_UNMEASURED_RADIUS = 2.2;

/**
 * Largest patch, in metres.
 *
 * Nothing else in the floor stack grows with the disc: the contact pool is a
 * plane of `GROUND_SHADOW.scale`, and the cast shadow has its own fixed
 * frustum. Past those a wider disc is just more lit plaster with nothing
 * under it. The tile is 3 m as well, so by 6 m across the repeat is two tiles
 * and starts to read as a pattern (see `FLOOR_TILE_M`).
 *
 * The disc is drawn into the ambient occlusion G-buffer — GTAO renders it
 * through `scene.overrideMaterial`, so a transparent floor counts as solid
 * ground there. It used to be backed by a 6 m plane sized from this value so
 * its rim could not hang over open space in that pass; that plane is gone,
 * measured as making no difference to the image.
 */
export const STUDIO_FLOOR_MAX_RADIUS = 3;

/**
 * Distance from the patch's centre to the furthest corner of the product's
 * footprint, in metres.
 *
 * The floor is drawn at the origin and {@link ProductFloorAligner} only
 * aligns y, so a product that sits off-centre in x/z needs the further side
 * measured, not half the box.
 */
export function footprintRadiusFromBox(box: Box3): number {
  if (box.isEmpty()) return 0;
  const x = Math.max(Math.abs(box.min.x), Math.abs(box.max.x));
  const z = Math.max(Math.abs(box.min.z), Math.abs(box.max.z));
  return Math.hypot(x, z);
}

/**
 * Radius of the floor patch for a product of the given footprint.
 *
 * The product has to stand on floor that is still fully opaque, otherwise
 * its corners are on a surface already fading into the page background and it
 * stops looking like it is standing anywhere — which is what a fixed 2.2 m
 * patch did from roughly 2000 x 800 mm up. So the footprint is divided by the
 * opaque fraction rather than used as the radius directly.
 *
 * Continuous, with no quantum: `StudioFloor` scales one unit circle rather
 * than rebuilding its geometry, so every intermediate value of a drag is free
 * and the patch can track the case frame by frame.
 *
 * {@link STUDIO_FLOOR_MAX_RADIUS} wins over all of this. Past a footprint
 * radius of `STUDIO_FLOOR_MAX_RADIUS * solid` — 1.35 m at the defaults, about
 * a 2600 x 800 mm case — the patch stops growing and the corners do begin to
 * fade. That is deliberate, and gentle: the fade is a smoothstep, so even the
 * largest realistic case leaves its corners at well over 90% opacity, which
 * reads better than a disc that outruns the shadow layers under it.
 */
export function studioFloorRadiusForFootprint(
  footprintRadiusM: number,
  /** Must be the `solid` the patch is actually drawn with, or the product
   * lands in the fade this function exists to keep it out of. */
  solid: number = STUDIO_FLOOR_SOLID,
): number {
  if (!Number.isFinite(footprintRadiusM) || footprintRadiusM <= 0) {
    // Through the same ceiling as the measured path below, even though 2.2 is
    // under it today, so that an unmeasured product cannot get a disc the
    // clamped path would refuse.
    return Math.min(STUDIO_FLOOR_MAX_RADIUS, STUDIO_FLOOR_UNMEASURED_RADIUS);
  }
  return Math.min(
    STUDIO_FLOOR_MAX_RADIUS,
    Math.max(STUDIO_FLOOR_MIN_RADIUS, footprintRadiusM / solid),
  );
}

/**
 * Per-scene footprint, published by the floor aligner and read by the ground
 * layers. A WeakMap for the same reason `sceneActivity` uses one: the entry
 * has to disappear with the scene, and neither side owns the other.
 *
 * The aligner already computes the product's bounding box on every scene
 * revision in order to sit it on the floor, so this costs no extra traversal.
 */
const footprints = new WeakMap<Scene, number>();

export function publishProductFootprintRadius(
  scene: Scene,
  radiusM: number,
): void {
  footprints.set(scene, radiusM);
}

/**
 * The product's world bounds, from the same measurement as the footprint.
 *
 * Kept beside it rather than measured again: the shadow caster proxy needs the
 * whole box, not just its ground radius, and the aligner is the one place the
 * product is already traversed on every revision.
 */
const productBounds = new WeakMap<Scene, Box3>();

export function publishProductBounds(scene: Scene, box: Box3): void {
  // Copied, not stored by reference: the aligner reuses one Box3 across frames
  // and would otherwise mutate what every reader sees.
  const stored = productBounds.get(scene);
  if (stored) stored.copy(box);
  else productBounds.set(scene, box.clone());
}

/** Null until a product has been measured in this scene. */
export function readProductBounds(scene: Scene): Box3 | null {
  return productBounds.get(scene) ?? null;
}

/**
 * Zero until the first product in this scene has been measured — callers fall
 * back to {@link STUDIO_FLOOR_UNMEASURED_RADIUS}, not to the minimum.
 *
 * Only until the first one, though: the entry is never cleared, and the
 * aligner skips the publish while the product's box is empty. So a rebuild or
 * a product swap keeps serving the previous footprint until the new one
 * measures, rather than falling back here. That is deliberate — the fallback
 * is a 2.2 m disc, and snapping to it for the frames of a rebuild would be a
 * visible pop — but it means this fallback is reachable once per scene.
 */
export function readProductFootprintRadius(scene: Scene): number {
  return footprints.get(scene) ?? 0;
}
